import { env } from 'config/env';
import { posthog } from 'config/posthog';

import type { AuthMeResponse } from '@brightlocal/api-client';

import { waitForFreshFeatureFlags } from './featureFlags';

/** Must match the group type configured in the PostHog project. */
const CUSTOMER_GROUP_TYPE = 'customer';

/**
 * Identifies the authenticated user and their `customer` group in PostHog,
 * then awaits the feature-flag reload so downstream callers (e.g. route
 * `beforeLoad` guards) see flags evaluated against the real identity.
 *
 * Safe to call on every authenticated navigation — a fast no-op when the
 * user and customer group already match.
 */
export const identifyPostHogUser = async (authData: AuthMeResponse) => {
  if (!env.posthog.enabled) return;

  const userId = String(authData.user.userId);
  const customerId = String(authData.customer.customerId);

  const isSameUser = posthog.get_distinct_id() === userId;
  const isSameCustomerGroup = posthog.getGroups()[CUSTOMER_GROUP_TYPE] === customerId;

  if (isSameUser && isSameCustomerGroup) return;

  // Group before identify, same tick — posthog-js coalesces both into one
  // `/flags` reload that carries the group context.
  posthog.group(CUSTOMER_GROUP_TYPE, customerId);

  posthog.identify(userId, {
    email: authData.user.email,
    customerId,
    packageId: authData.subscription.packageId
  });

  // Wait for that reload so the caller can read flags synchronously.
  await waitForFreshFeatureFlags();
};

/**
 * Resets PostHog's identified user state, including the persisted `customer`
 * group. Call on logout so the next session starts anonymous with freshly
 * evaluated flags.
 */
export const clearPostHogUser = () => {
  if (!env.posthog.enabled) return;
  posthog.reset();
};
